import React, { FC, useCallback } from 'react';

import { useNavigate } from 'react-router-dom';

import { ICartProperty } from '@rbi-ctg/offers';
import Modal, { ModalContent } from 'components/modal';
import {
  CateringDeliveryButton,
  CateringPickupButton,
  DeliveryButton,
  PickupButton,
} from 'components/service-mode-button';
import { ModalHeading } from 'components/service-mode-button/prompts';
import { useGeolocation } from 'state/geolocation';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useOffersContext } from 'state/offers';
import { ServiceMode, useOrderContext } from 'state/order';
import { IOrder } from 'state/order/types';
import { useStoreContext } from 'state/store';
import { GeolocationPermissionStates } from 'utils/geolocation';
import noop from 'utils/noop';
import { RuleType, findAllRulesOfTypeThatApply } from 'utils/offers';
import { routes } from 'utils/routing';

const ServiceModeModal: FC = () => {
  const { selectedOffer } = useOffersContext();
  const { selectServiceMode: orderContextSelectServiceMode }: IOrder = useOrderContext();
  const { resetStore } = useStoreContext();
  const enableDelivery = useFlag(LaunchDarklyFlag.ENABLE_DELIVERY);
  const enableOrdering = useFlag(LaunchDarklyFlag.ENABLE_ORDERING);
  const enableCatering = useFlag(LaunchDarklyFlag.ENABLE_CATERING);
  const enableCateringDelivery = useFlag(LaunchDarklyFlag.ENABLE_CATERING_DELIVERY);
  const { permission, loadCurrentPosition } = useGeolocation();
  const navigate = useNavigate();

  const selectedOfferUnavailableForPickup = useCallback(() => {
    if (!selectedOffer) {
      return false;
    }
    // get an array of all Cart Property ruleSets
    const allCartPropertyRuleSetParams = findAllRulesOfTypeThatApply(
      RuleType.CartProperty,
      selectedOffer.ruleSet
    ).map((ruleSet: any) => ruleSet.params);
    // filter the above array for ruleSets that meet the below Criteria
    const filteredRuleSets = allCartPropertyRuleSetParams.filter((param: ICartProperty) => {
      const { operator, key, negated, value } = param ?? {};
      return (
        operator === '=' &&
        key === 'serviceMode' &&
        negated === true &&
        (value === ServiceMode.DRIVE_THRU ||
          value === ServiceMode.TAKEOUT ||
          value === ServiceMode.EAT_IN)
      );
    });
    // this check assumes that if all of the above service modes are unavailable, this offer is unavailable for pickup
    if (filteredRuleSets.length >= 3) {
      return true;
    }
    return false;
  }, [selectedOffer]);

  const selectedOfferUnavailableForDelivery = useCallback(() => {
    if (!selectedOffer) {
      return false;
    }

    const allCartPropertyRuleSetParams = findAllRulesOfTypeThatApply(
      RuleType.CartProperty,
      selectedOffer.ruleSet
    ).map((ruleSet: any) => ruleSet.params);

    const filteredRuleSets = allCartPropertyRuleSetParams.filter((param: ICartProperty) => {
      const { operator, key, negated, value } = param ?? {};
      return (
        operator === '=' &&
        key === 'serviceMode' &&
        negated === true &&
        value === ServiceMode.DELIVERY
      );
    });
    if (filteredRuleSets.length >= 1) {
      return true;
    }
    return false;
  }, [selectedOffer]);

  const navigateForServiceMode = (mode: ServiceMode | null) =>
    mode === ServiceMode.DELIVERY ? navigate(routes.address) : navigate(routes.storeLocator);

  const selectServiceMode = (mode: ServiceMode | null) => {
    orderContextSelectServiceMode(mode).then((updatedSvcMode: boolean) => {
      if (!updatedSvcMode) {
        return;
      }
      if (mode === ServiceMode.CATERING_PICKUP) {
        navigate(routes.serviceModeCateringPickup);

        return;
      }
      if (mode === ServiceMode.CATERING_DELIVERY) {
        navigate(routes.serviceModeCateringDelivery);

        return;
      }
      resetStore();

      if (permission === GeolocationPermissionStates.DENIED) {
        navigateForServiceMode(mode);

        return;
      }

      if (permission === GeolocationPermissionStates.GRANTED) {
        // TODO: Implement a properly designed loading state
        loadCurrentPosition().catch(noop);
        navigateForServiceMode(mode);

        return;
      }

      navigate(routes.locationService);
    });
  };

  if (permission === GeolocationPermissionStates.CHECKING) {
    return null;
  }

  return !enableOrdering ? null : (
    <Modal
      onDismiss={() => {
        selectServiceMode(null);
        navigate('../');
      }}
      mParticleEventData={{
        modalAppearanceEventMessage: 'Select Service Mode',
      }}
    >
      <ModalContent>
        <ModalHeading />
        <DeliveryButton
          enableButton={enableDelivery && !selectedOfferUnavailableForDelivery()}
          selectServiceMode={() => {
            navigate(routes.address);
          }}
          offerUnavailableForDelivery={selectedOfferUnavailableForDelivery()}
        />
        <PickupButton
          offerUnavailableForPickup={selectedOfferUnavailableForPickup()}
          selectServiceMode={() => selectServiceMode(ServiceMode.TAKEOUT)}
          enableButton={!selectedOfferUnavailableForPickup()}
        />
        {enableCatering && (
          <>
            <CateringPickupButton
              selectServiceMode={() => selectServiceMode(ServiceMode.CATERING_PICKUP)}
            />
            <CateringDeliveryButton
              enableButton={enableCateringDelivery}
              selectServiceMode={() => selectServiceMode(ServiceMode.CATERING_DELIVERY)}
            />
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default ServiceModeModal;
